<template>
  <ToggleCard
    v-bind="{ disabled, name, variant }"
    v-model="model"
    class="high-frequency-game-add-on"
    container-id="high_frequency_game_add_on_form"
    :info-button-aria-label="$t('infobutton.conveniencefee.label')"
    :info-headline="
      $t(`highfrequencygame.addontoggle.${name}.info.headline`, {
        addOn: translatedAddOn,
      })
    "
    :info-text="
      $t(`highfrequencygame.addontoggle.${name}.info.text`, {
        addOn: translatedAddOn,
      })
    "
    :switch-aria-label-text="$t('highfrequencygame.addontoggle.default.switch')"
  >
    <template #headline>{{ translatedAddOn }}</template>

    <div class="addon-footer flex flex-grow items-center justify-between">
      <span v-if="state === 'DISABLED'" class="caption-regular text-primary-primary">
        {{ $t(`highfrequencygame.addontoggle.${name}.disabled.text`) }}
      </span>

      <span v-if="state === 'INACTIVE'" class="caption-regular text-secondary-secondary">
        {{ $t(`highfrequencygame.addontoggle.${name}.inactive.text`) }}
      </span>

      <div v-if="state === 'ACTIVE'" class="flex flex-col">
        <span class="caption-bold text-accent-accent-success">
          {{ $t(`highfrequencygame.addontoggle.${name}.active.headline`) }}
        </span>
        <span class="caption-regular text-secondary-secondary">
          {{
            $t(`highfrequencygame.addontoggle.active.subline`, {
              value: getMaxMultiplier(name),
            })
          }}
        </span>
      </div>

      <span
        v-if="['ACTIVE', 'INACTIVE'].includes(state)"
        class="paragraph-small-text py-050 px-100 rounded-s bg-secondary-secondary text-primary-primary text-right"
      >
        {{ $t(`highfrequencygame.addontoggle.${name}.badge.text`) }}
      </span>
    </div>
  </ToggleCard>
</template>

<script lang="ts" setup>
import type { ToggleCardVariantStyle } from '~/components/Toggle/types'

type Props = { disabled?: boolean; name: string | VariantKey }

type VariantKey = keyof typeof TOGGLE_CARD_VARIANTS

const TOGGLE_CARD_VARIANTS = {
  kenoBonus: {
    gradient: {
      bottom: { color: '#fbcd0c66', stop: '101.98%' },
      top: { color: '#FBCD0C', stop: '0.13%' },
    },
    illustration: {
      active: 'bonus-active',
      inactive: 'bonus',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
  kenoBooster: {
    gradient: {
      bottom: { color: '#fbcd0c66', stop: '101.98%' },
      top: { color: '#FBCD0C', stop: '0.13%' },
    },
    illustration: {
      active: 'booster-active',
      inactive: 'booster',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
  kenoMultiplier: {
    gradient: {
      bottom: { color: '#5AB0DD', stop: '101.98%' },
      top: { color: '#5AB0DD', stop: '0.13%' },
    },
    illustration: {
      active: 'multiplier-active',
      inactive: 'multiplier',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
} as const satisfies Record<string, ToggleCardVariantStyle>

const { disabled = false, name } = defineProps<Props>()
const model = defineModel({ default: false, type: Boolean })

const { t } = useI18n()
const { config } = useHighFrequencyGame()

const translatedAddOn = computed(() => t(`highfrequencygame.addon.${name}`))

const state = computed(() => {
  if (disabled) return 'DISABLED'
  return model.value ? 'ACTIVE' : 'INACTIVE'
})

const variant = computed(() => {
  if (!hasToggleCardVariant(name)) return
  return TOGGLE_CARD_VARIANTS[name]
})

const hasToggleCardVariant = (name: string): name is VariantKey =>
  TOGGLE_CARD_VARIANTS[name as VariantKey] !== undefined

const getMaxMultiplier = (name: string) =>
  config.value?.addOns.find((addOn) => addOn.name === name)?.maximumMultiplier ?? 0
</script>

<style lang="scss" scoped>
.high-frequency-game-add-on {
  min-height: 6.75rem;
}

.addon-footer {
  min-height: 2rem;
}
</style>
